import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Rocket } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChipSelect } from "@/components/ChipSelect";
import { TagInput } from "@/components/TagInput";
import { Loader } from "@/components/states";
import * as O from "@/pages/onboarding/options";

const STEPS = [
  "Welcome", "Profile", "Interests", "Expertise", "Style",
  "Format", "Goals", "Constraints", "Niche", "Connections",
];

const EMPTY = {
  creator_name: "", age_range: null, location: "", language: null,
  interests: [], skills: [], expertise: [], subjects: [], experience_level: null,
  content_styles: [], content_formats: [], goals: [],
  constraints: { creation_time: null, editing_skill: null, equipment_level: null, budget: null, preferred_platforms: [] },
  niche: { mode: null, value: "", categories: [] },
};

export default function Onboarding() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/onboarding");
        if (data.completed) {
          navigate("/dashboard");
          return;
        }
        const p = data.profile || {};
        setForm({
          ...EMPTY,
          ...p,
          creator_name: p.creator_name || "",
          location: p.location || "",
          constraints: { ...EMPTY.constraints, ...(p.constraints || {}) },
          niche: { ...EMPTY.niche, ...(p.niche || {}), value: p.niche?.value || "" },
        });
        setStep(Math.min(data.step || 0, STEPS.length - 1));
      } catch (err) {
        toast.error(apiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setConstraint = (patch) => setForm((f) => ({ ...f, constraints: { ...f.constraints, ...patch } }));
  const setNiche = (patch) => setForm((f) => ({ ...f, niche: { ...f.niche, ...patch } }));

  const persist = async (nextStep, extra = {}) => {
    setSaving(true);
    try {
      await api.put("/onboarding", { step: nextStep, data: { ...form, ...extra } });
      return true;
    } catch (err) {
      toast.error(apiErrorMessage(err));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const next = async () => {
    const target = Math.min(step + 1, STEPS.length - 1);
    if (await persist(target)) setStep(target);
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const finish = async () => {
    if (!(await persist(STEPS.length - 1))) return;
    setSaving(true);
    try {
      await api.post("/onboarding/complete");
      toast.success("Creator DNA saved");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader label="Loading onboarding" fullscreen />;

  const pct = Math.round(((step + 1) / STEPS.length) * 100);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b border-border bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5">
          <Logo />
          <span className="rounded-full bg-elevated px-3 py-1 text-xs font-medium text-muted-foreground">
            Step {step + 1} of {STEPS.length}
          </span>
        </div>
        {/* Progress bar */}
        <div className="h-1 w-full bg-elevated">
          <div className="h-full bg-accent transition-[width] duration-500 ease-out"
            style={{ background: "linear-gradient(90deg, #E8522A, #F2744A)", width: `${pct}%` }} />
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-5 py-10 fade-in" data-testid={`onboarding-step-${step}`}>
        <StepBody step={step} form={form} set={set} setConstraint={setConstraint} setNiche={setNiche} />

        <div className="mt-10 flex items-center justify-between border-t border-border pt-6">
          <Button variant="ghost" onClick={back} disabled={step === 0 || saving}
            className="rounded-full" data-testid="onboarding-back">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          <div className="flex items-center gap-2">
            {step === STEPS.length - 1 ? (
              <>
                <Button variant="outline" onClick={finish} disabled={saving}
                  className="rounded-full" data-testid="onboarding-skip">Skip for now</Button>
                <Button onClick={finish} disabled={saving} data-testid="onboarding-finish"
                  className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm">
                  <Rocket className="mr-2 h-4 w-4" /> {saving ? "Finishing…" : "Enter Virality"}
                </Button>
              </>
            ) : (
              <Button onClick={next} disabled={saving} data-testid="onboarding-next"
                className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm">
                {saving ? "Saving…" : "Continue"} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function Header({ eyebrow, title, description }) {
  return (
    <div className="mb-8">
      <span className="inline-block rounded-full bg-accent/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-accent">
        {eyebrow}
      </span>
      <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-foreground">{title}</h1>
      {description && <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{description}</p>}
    </div>
  );
}

function Group({ label, children }) {
  return (
    <div className="space-y-2.5">
      <label className="text-sm font-semibold text-foreground">{label}</label>
      {children}
    </div>
  );
}

function StepBody({ step, form, set, setConstraint, setNiche }) {
  switch (step) {
    case 0:
      return (
        <>
          <Header eyebrow="Welcome" title="Let's build your Creator DNA."
            description="Virality uses your Creator DNA to power personalized opportunities, ideas and recommendations. This takes about 2 minutes and you can edit everything later." />
          <div className="grid gap-3 overflow-hidden sm:grid-cols-3">
            {["Identity & niche", "Style & goals", "Constraints & platforms"].map((t, i) => (
              <div key={t} className="rounded-2xl border border-border bg-surface p-5 shadow-sm">
                <p className="font-display text-2xl font-extrabold text-accent/40">0{i + 1}</p>
                <p className="mt-2 text-sm font-medium text-foreground">{t}</p>
              </div>
            ))}
          </div>
        </>
      );
    case 1:
      return (
        <>
          <Header eyebrow="Creator profile" title="Tell us who you are." />
          <div className="space-y-6">
            <Group label="Creator name">
              <Input value={form.creator_name} onChange={(e) => set({ creator_name: e.target.value })}
                placeholder="How you want to be known" data-testid="ob-creator-name" />
            </Group>
            <Group label="Age range">
              <ChipSelect single options={O.AGE_RANGES} value={form.age_range}
                onChange={(v) => set({ age_range: v })} testid="ob-age" />
            </Group>
            <Group label="Location">
              <Input value={form.location} onChange={(e) => set({ location: e.target.value })}
                placeholder="City, Country" data-testid="ob-location" />
            </Group>
            <Group label="Primary language">
              <ChipSelect single options={O.LANGUAGES} value={form.language}
                onChange={(v) => set({ language: v })} testid="ob-language" />
            </Group>
          </div>
        </>
      );
    case 2:
      return (
        <>
          <Header eyebrow="Interests" title="What topics do you care about?"
            description="Select all that apply." />
          <ChipSelect options={O.INTERESTS} value={form.interests}
            onChange={(v) => set({ interests: v })} testid="ob-interests" />
        </>
      );
    case 3:
      return (
        <>
          <Header eyebrow="Skills & expertise" title="What can you credibly talk about?" />
          <div className="space-y-6">
            <Group label="Skills"><TagInput value={form.skills} onChange={(v) => set({ skills: v })} testid="ob-skills" /></Group>
            <Group label="Areas of expertise"><TagInput value={form.expertise} onChange={(v) => set({ expertise: v })} testid="ob-expertise" /></Group>
            <Group label="Subjects you can discuss"><TagInput value={form.subjects} onChange={(v) => set({ subjects: v })} testid="ob-subjects" /></Group>
            <Group label="Experience level">
              <ChipSelect single options={O.EXPERIENCE_LEVELS} value={form.experience_level}
                onChange={(v) => set({ experience_level: v })} testid="ob-experience" />
            </Group>
          </div>
        </>
      );
    case 4:
      return (
        <>
          <Header eyebrow="Content style" title="How do you like to show up?" description="Select all that apply." />
          <ChipSelect options={O.CONTENT_STYLES} value={form.content_styles}
            onChange={(v) => set({ content_styles: v })} testid="ob-styles" />
        </>
      );
    case 5:
      return (
        <>
          <Header eyebrow="Content format" title="Which formats do you create?" description="Select all that apply." />
          <ChipSelect options={O.CONTENT_FORMATS} value={form.content_formats}
            onChange={(v) => set({ content_formats: v })} testid="ob-formats" />
        </>
      );
    case 6:
      return (
        <>
          <Header eyebrow="Goals" title="What are you optimizing for?" description="Select all that apply." />
          <ChipSelect options={O.GOALS} value={form.goals} onChange={(v) => set({ goals: v })} testid="ob-goals" />
        </>
      );
    case 7:
      return (
        <>
          <Header eyebrow="Creation constraints" title="How do you work?" />
          <div className="space-y-6">
            <Group label="Available creation time">
              <ChipSelect single options={O.CREATION_TIME} value={form.constraints.creation_time}
                onChange={(v) => setConstraint({ creation_time: v })} testid="ob-time" />
            </Group>
            <Group label="Editing skill">
              <ChipSelect single options={O.EDITING_SKILL} value={form.constraints.editing_skill}
                onChange={(v) => setConstraint({ editing_skill: v })} testid="ob-editing" />
            </Group>
            <Group label="Equipment level">
              <ChipSelect single options={O.EQUIPMENT_LEVEL} value={form.constraints.equipment_level}
                onChange={(v) => setConstraint({ equipment_level: v })} testid="ob-equipment" />
            </Group>
            <Group label="Budget">
              <ChipSelect single options={O.BUDGET} value={form.constraints.budget}
                onChange={(v) => setConstraint({ budget: v })} testid="ob-budget" />
            </Group>
            <Group label="Preferred platforms">
              <ChipSelect options={O.PLATFORMS} value={form.constraints.preferred_platforms}
                onChange={(v) => setConstraint({ preferred_platforms: v })} testid="ob-platforms" />
            </Group>
          </div>
        </>
      );
    case 8:
      return (
        <>
          <Header eyebrow="Niche" title="Where do you want to win?"
            description="Enter your niche, pick from suggestions, or explore later — your call." />
          <div className="space-y-6">
            <Group label="Your niche">
              <Input value={form.niche.value}
                onChange={(e) => setNiche({ value: e.target.value, mode: "existing" })}
                placeholder="e.g. AI tools for solo founders" data-testid="ob-niche-value" />
            </Group>
            <Group label="Or pick suggested categories">
              <ChipSelect options={O.NICHE_CATEGORIES} value={form.niche.categories}
                onChange={(v) => setNiche({ categories: v, mode: form.niche.value ? "existing" : "suggested" })}
                testid="ob-niche-categories" />
            </Group>
            <button type="button" onClick={() => setNiche({ mode: "later" })}
              className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              data-testid="ob-niche-later">
              I'll explore niches later
            </button>
          </div>
        </>
      );
    case 9:
      return (
        <>
          <Header eyebrow="Social connections" title="Connect accounts — or skip."
            description="Connecting is optional. You can use intelligence, research, ideation, hooks, scripts and recording without connecting anything. Connect later from the Connections page." />
          <div className="grid gap-3 sm:grid-cols-2">
            {O.PLATFORMS.map((p) => (
              <div key={p} className="flex items-center justify-between rounded-2xl border border-border bg-surface px-4 py-3 shadow-sm">
                <span className="text-sm font-medium text-foreground">{p}</span>
                <span className="rounded-full bg-elevated px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Connect later
                </span>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-2 rounded-2xl border border-border bg-elevated px-4 py-3 text-sm text-muted-foreground">
            <Check className="h-4 w-4 text-accent" /> Skipping is completely fine — connect from the Connections page anytime.
          </div>
        </>
      );
    default:
      return null;
  }
}
